import PageHeader from "Components/Pages/Header";
import Breadcrumb from "Components/Shared/Breadcrumb";
import { Button } from "Components/Button";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router";
import { Loader } from "Components/Loader";
import {
  getPartnerByIdAPI,
  updatePartnersAPI,
  uploadLogoPartnerAPI,
} from "Requests/Partners";
import { createToast } from "Utils/toastFunc";
import { formatUnitsRequest, getInitialUnitsValue } from "Utils/Units/Units";
import { useRegionsClientDomicile } from "Hooks/useRegionsClientDomicile";
import { PartnerForm, PartnerFormValues } from "../components/PartnerForm";
import * as S from "../styles";

type PartnerFormEditValues = PartnerFormValues;

export const EditPartner = () => {
  const { partnerId } = useParams<{ partnerId: string }>();

  const history = useHistory();

  const client = useQueryClient();

  const { data, isLoading } = useQuery({
    queryFn: () => getPartnerByIdAPI(partnerId),
    queryKey: ["parter-id", partnerId],
    refetchOnWindowFocus: false,
  });

  const { regions } = useRegionsClientDomicile();

  const { id } = data ?? {};

  const updatePartnersMutation = useMutation({
    mutationFn: updatePartnersAPI,
    onSuccess: (data) => {
      client.removeQueries(["partners-list"]);
      client.setQueryData(["parter-id", partnerId], () => ({
        ...data,
      }));
    },
  });

  const uploadLogoPartnerMutation = useMutation({
    mutationFn: uploadLogoPartnerAPI,
  });

  const handleSubmit = async (data: PartnerFormEditValues) => {
    try {
      const appointments = data?.appointmentType?.map((appointment: any) => ({
        name: appointment.name,
        id: appointment.appointment
          ? Number(appointment.appointment.replace(/\D/g, ""))
          : 0,
      }));

      const hasAppointmentInfo = data?.appointmentType.some(
        (appointment) => appointment.name
      );

      const hasIntegrationCode =
        data.integration_codes.length && data.integration_codes[0].id;

      const partnerUpdated = {
        name: data.name,
        pre_flow: data.pre_flow,
        login: data.login ? data.login : null,
        ...(hasAppointmentInfo && { appointments }),
        auth_client_id: data.auth_client_id ? data.auth_client_id : null,
        ...(data.categories.length >= 1 && {
          categories: data.categories.map((category) => category.id),
        }),
        ...(data.prod_include.length >= 1 && {
          prod_include: data.prod_include.map((product) => product.id),
        }),
        ...(data.prod_exclude.length >= 1 && {
          prod_exclude: data.prod_exclude.map((product) => product.id),
        }),
        product_needs_authorization: data.product_needs_authorization,
        free_visit: !data.free_visit,
        ...(hasIntegrationCode && {
          integration_codes: data.integration_codes,
        }),
        units: {
          ...formatUnitsRequest(data.units, regions),
        },
      };

      if (id) {
        await updatePartnersMutation.mutateAsync({
          partnerId: id,
          partner: partnerUpdated,
        });

        const formData = new FormData();
        formData.append("file", data.attach as File);
        await uploadLogoPartnerMutation.mutateAsync({
          partnerId: id,
          file: formData,
        });
      }

      createToast("success", "Parceiro atualizado com sucesso");
      history.push("/parceiros");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors[0]?.message;
      createToast(
        "error",
        errorMessage || "Não foi possível editar este parceiro"
      );
    }
  };

  const appointmentInfo = data?.appointments
    ? data.appointments.map((appointment: { id: number; name: string }) => ({
        appointment: String(appointment.id),
        name: appointment.name,
      }))
    : [{ appointment: "", name: "" }];

  const prod_include = data?.prod_include
    ? data.prod_include.map((item) => ({
        id: item.product_id,
        product: item.product,
        sku: item.sku,
      }))
    : [];

  const prod_exclude = data?.prod_exclude
    ? data.prod_exclude.map((item) => ({
        id: item.product_id,
        product: item.product,
        sku: item.sku,
      }))
    : [];

  const categories = data?.categories
    ? data.categories.map((category) => ({
        name: category.category,
        id: category.category_id,
      }))
    : [];

  return (
    <S.Container className="entry-app-content">
      <div className="content-holder">
        <div>
          <div className="wrapper-header-content">
            <PageHeader title="Editar parceiro">
              <Breadcrumb
                list={[
                  {
                    path: "/parceiros",
                    label: "Parceiros",
                    current: false,
                  },
                  {
                    path: "/criar-parceiro",
                    label: "Editar parceiro",
                    current: true,
                  },
                ]}
              />
            </PageHeader>
            <div className="buttons-container">
              <>
                <Button
                  outlined
                  borderRadius="111"
                  onClick={() => history.push("/parceiros")}
                  disabled={updatePartnersMutation.isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  form="partner-form"
                  type="submit"
                  borderRadius="111"
                  loading={updatePartnersMutation.isLoading}
                  disabled={updatePartnersMutation.isLoading}
                >
                  Salvar
                </Button>
              </>
            </div>
          </div>
        </div>

        <main>
          <div className="form">
            {isLoading && (
              <div className="flex">
                <Loader />
              </div>
            )}

            {data?.id && (
              <PartnerForm
                onSubmit={handleSubmit}
                initialValues={{
                  name: data.name,
                  attach: data.path_logo || null,
                  appointmentType: appointmentInfo,
                  integration_codes: data.integration_codes
                    ? data.integration_codes
                    : [{ id: "", name: "" }],
                  login: data.login || "",
                  auth_client_id: data.auth_client_id || "",
                  free_visit: !data.free_visit,
                  pre_flow: data.pre_flow,
                  prod_include,
                  categories,
                  prod_exclude,
                  product_needs_authorization: data.product_needs_authorization,
                  units: getInitialUnitsValue(data?.units, regions),
                }}
              />
            )}
          </div>
        </main>
      </div>
    </S.Container>
  );
};
